import { useEffect, useMemo, useState } from 'react';

import {
  DetailsAddress,
  DetailsCard,
  DetailsDialog,
  DetailsValues,
} from '../types';

export type UseDetailsFormArgs = {
  addresses: DetailsAddress[];
  cards: DetailsCard[];
  /** Adopted once as the initial selections when the saved lists arrive. */
  defaultAddressId?: string;
  defaultCardId?: string;
  isPreOrderFlow: boolean;
};

export type DetailsFormController = {
  values: DetailsValues;
  selectedAddress?: DetailsAddress;
  selectedCard?: DetailsCard;
  openDialog: DetailsDialog;
  setSelectedAddressId: (id: string) => void;
  setSaveAddress: (saveAddress: boolean) => void;
  setSelectedCardId: (id: string) => void;
  setBillingSameAsShipping: (billingSameAsShipping: boolean) => void;
  setSaveCard: (saveCard: boolean) => void;
  setPreOrderOptIn: (preOrderOptIn: boolean) => void;
  open: (dialog: DetailsDialog) => void;
  close: () => void;
};

export const useDetailsForm = ({
  addresses,
  cards,
  defaultAddressId,
  defaultCardId,
  isPreOrderFlow,
}: UseDetailsFormArgs): DetailsFormController => {
  const [selectedAddressId, setSelectedAddressId] = useState(addresses[0]?.id);
  const [saveAddress, setSaveAddress] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState(cards[0]?.id);
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [saveCard, setSaveCard] = useState(true);
  const [preOrderOptIn, setPreOrderOptIn] = useState(isPreOrderFlow);
  const [openDialog, setOpenDialog] = useState<DetailsDialog | null>(null);

  useEffect(() => {
    if (!defaultAddressId) return;
    setSelectedAddressId((previous) => previous ?? defaultAddressId);
  }, [defaultAddressId]);

  useEffect(() => {
    if (!defaultCardId) return;
    setSelectedCardId((previous) => previous ?? defaultCardId);
  }, [defaultCardId]);

  const selectedAddress = useMemo(
    () => addresses.find(({ id }) => id === selectedAddressId),
    [addresses, selectedAddressId],
  );

  const selectedCard = useMemo(
    () => cards.find(({ id }) => id === selectedCardId),
    [cards, selectedCardId],
  );

  return {
    close: () => setOpenDialog(null),
    open: setOpenDialog,
    openDialog,
    selectedAddress,
    selectedCard,
    setBillingSameAsShipping,
    setPreOrderOptIn,
    setSaveAddress,
    setSaveCard,
    setSelectedAddressId,
    setSelectedCardId,
    values: {
      billingSameAsShipping,
      preOrderOptIn,
      saveAddress,
      saveCard,
      selectedAddressId,
      selectedCardId,
    },
  };
};
